import React, { useState, useRef } from "react";
import tw from "twin.macro";
import "react-responsive-modal/styles.css";
import { Modal } from "react-responsive-modal";
import { PrimaryButton as PrimaryButtonBase } from "../misc/Buttons";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import ReCAPTCHA from "react-google-recaptcha";

// Styled Components
const Container = tw.div`text-center text-black px-4 sm:px-6`;
const Heading = tw.h4`text-xl sm:text-2xl lg:text-3xl font-bold tracking-wide text-center mb-4 leading-tight`;
const Subheading = tw.p`text-base sm:text-lg font-medium text-gray-600 text-center mb-8 leading-relaxed max-w-lg mx-auto`;

const Form = tw.form`mt-6 text-sm max-w-md mx-auto w-full`;
const InputGroup = tw.div`grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6`;
const Input = tw.input`bg-white px-4 py-4 font-medium rounded-lg border-2 border-gray-300 hover:border-primary-400 focus:border-primary-500 focus:outline-none transition duration-300 w-full text-base`;
const SubmitButton = tw(
    PrimaryButtonBase
)`w-full mt-5 px-8 py-4 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition duration-300 text-base`;

const ErrorText = tw.p`text-red-600 text-sm mt-4 text-center`;
const FieldError = tw.small`text-red-500 text-xs mt-1 block`;

// Yup Validation Schema
const validationSchema = Yup.object().shape({
    fname: Yup.string().min(2, "First name too short").required("First name is required"),
    sname: Yup.string().min(2, "Surname too short").required("Surname is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
});

const RegistrationForm = ({ onClose, onSuccess }) => {
    const [errorMsg, setErrorMsg] = useState("");
    const recaptchaRef = useRef();

    const formik = useFormik({
        initialValues: {
            fname: "",
            sname: "",
            email: "",
            password: "",
            userType: "PATIENT",
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setErrorMsg(""); // reset error
            
            // Get reCAPTCHA token
            const recaptchaToken = recaptchaRef.current.getValue();
            if (!recaptchaToken) {
                setErrorMsg("Please complete the reCAPTCHA verification.");
                setSubmitting(false);
                return;
            }
            
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_HTTP_DOMAIN}/register`,
                    {...values, marketingOptIn: true, recaptchaToken},
                    {
                        headers: { "Content-Type": "application/json" },
                    }
                );

                if (response.status === 201) {
                    onSuccess();
                    onClose();
                } else {
                    setErrorMsg("Unexpected response from server.");
                }
            } catch (error) {
                const message = error.response?.data?.error || "Registration failed. Please try again.";
                setErrorMsg(message);
                console.error("Registration error:", message);
            } finally {
                setSubmitting(false);
            }
        },
    });

    return (
        <Modal
            open
            onClose={onClose}
            center
            styles={{
                modal: { 
                    padding: "20px", 
                    maxWidth: "500px", 
                    width: "90%",
                    borderRadius: "12px"
                },
            }}
        >
            <Container>
                <Heading>Start Moving Better, Feeling Younger – For Free</Heading>
                <Subheading>Get your first motion scan today. No payment needed.</Subheading>

                <Form onSubmit={formik.handleSubmit}>
                    <InputGroup>
                        <div>
                            <Input
                                type="text"
                                name="fname"
                                placeholder="First Name"
                                value={formik.values.fname}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.fname && formik.errors.fname && (
                                <FieldError>{formik.errors.fname}</FieldError>
                            )}
                        </div>

                        <div>
                            <Input
                                type="text"
                                name="sname"
                                placeholder="Last Name"
                                value={formik.values.sname}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                            {formik.touched.sname && formik.errors.sname && (
                                <FieldError>{formik.errors.sname}</FieldError>
                            )}
                        </div>
                    </InputGroup>

                    <div tw="mb-6">
                        <Input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={formik.values.email}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        />
                        {formik.touched.email && formik.errors.email && (
                            <FieldError>{formik.errors.email}</FieldError>
                        )}
                    </div>

                    <div style={{ margin: "20px 0px" }}>
                        <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY}
                        />
                    </div>

                    <SubmitButton type="submit" disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? "Creating Account..." : "Get Started Free"}
                    </SubmitButton>

                    {errorMsg && <ErrorText>{errorMsg}</ErrorText>}
                </Form>
            </Container>
        </Modal>
    );
};

export default RegistrationForm;
